import {
  useMemo,
  FC,
  ReactNode,
  useEffect,
  useState,
  useCallback,
  useRef,
} from 'react';
import { createContext, useContextSelector } from 'use-context-selector';
import { useSWRConfig } from 'swr';
import axios from 'axios';
import apiHandler from '@api/ApiHandler';
import { getUserData } from '@api/auth/apiService.ts';
import { UserResponse, LoginResponse } from '@api/auth/model/types.ts';
import { UserAISettings } from '@api/user/model/types';
import { Preloader } from '@components/Preloader/ui/Preloader.tsx';
import useLocalStorage from '@hooks/useLocalStorage';
import styles from '@components/PageTransition/ui/PageTransition.module.scss';

interface IAuthProviderProps {
  children: ReactNode;
}

interface AuthContextValues {
  userData: UserResponse | null;
  updateUserData: (data: UserResponse) => void;
  renewUserImageLink: () => void;
  accessToken: string | null;
  refreshToken: string | null;
  loading: boolean;
  login: (data: LoginResponse) => void;
  logout: () => void;
  userSettings: any; // Replace 'any' with a more specific type
  updateSettings: (data: any) => void;
}

/**
 * @type {import("react").Context<AuthContextValues>}
 * */

const AuthContext = createContext<AuthContextValues>({
  userData: null,
  accessToken: null,
  refreshToken: null,
  loading: false,
  login: () => {},
  logout: () => {},
  userSettings: null,
  updateSettings: () => {},
  updateUserData: () => {},
  renewUserImageLink: () => {},
});

// A signed picture link lasts an hour (ZLAYT-1479). One refetch per minute
// at most, however many pictures on the page fail at once, and none in a
// loop if the fresh link fails too.
const IMAGE_LINK_RENEW_INTERVAL_MS = 60 * 1000;

/**
 * @description Custom provider managing authentication state.
 */

export const AuthProvider: FC<IAuthProviderProps> = ({ children }) => {
  const [userData, setUserData] = useState<UserResponse | null>(null);
  const [accessToken, setAccessToken] = useLocalStorage('accessToken', null);
  const [refreshToken, setRefreshToken] = useLocalStorage('refreshToken', null);
  const [loading, setLoading] = useState(accessToken !== null);
  const [userSettings, setUserSettings] = useState<UserAISettings | null>(null);

  const { mutate } = useSWRConfig();

  /**
   * Drop every cached response.
   *
   * There is no SWRConfig wrapper, so SWR keeps one module level cache for the
   * whole app and it outlives a sign out. SWR renders what it has cached before
   * it revalidates, so the next person to sign in on this browser saw the
   * previous account's case list on My Documents until the new request came
   * back. Nothing leaked from the server, but one client's matters appeared
   * inside another account's session, which for a law firm is indistinguishable
   * from a breach.
   *
   * `revalidate: false` matters: without it SWR would immediately refetch every
   * key, including ones belonging to the account that just left.
   */
  const clearCachedData = useCallback(() => {
    // Only server responses. Some SWR keys are local UI state rather than
    // fetched data: the sidebar drawer flag is held under api/menumaster with
    // a local fetcher and every revalidation switched off. Clearing that one
    // set it to undefined and nothing ever refilled it, so the layout read a
    // property of undefined and the whole app threw on the next sign in until
    // the page was reloaded. It holds no customer data, so it is left alone.
    const LOCAL_STATE_KEYS = ['api/menumaster'];
    mutate(
      (key) =>
        typeof key === 'string'
          ? !LOCAL_STATE_KEYS.some((local) => key.startsWith(local))
          : true,
      undefined,
      { revalidate: false }
    );
  }, [mutate]);

  const login = useCallback(
    (data: LoginResponse) => {
      const { access, refresh } = data;
      if (!access) {
        return;
      }
      // Also cleared on the way in, because a session can end without a
      // deliberate sign out: an expired token drops the user at the login
      // screen with the previous account's responses still cached.
      clearCachedData();
      setAccessToken(access);
      setRefreshToken(refresh);
    },
    [clearCachedData]
  ); // setAccessToken and setRefreshToken are stable from useLocalStorage

  const logout = useCallback(() => {
    // Revoke the refresh token on the server (SOC 2), so a copy of it stops
    // working too. Fire and forget: signing out never waits on the network.
    if (refreshToken) {
      axios
        .post(`${import.meta.env.VITE_APP_API_URL}/user-auth/logout/`, {
          refresh: refreshToken,
        })
        .catch(() => {});
    }
    setAccessToken(null);
    setRefreshToken(null);
    setUserData(null);
    setUserSettings(null);
    apiHandler.removeAuthToken();
    clearCachedData();
  }, [clearCachedData, refreshToken]); // setAccessToken, setRefreshToken, and setUserData are stable

  const updateUserData = useCallback((data: UserResponse) => {
    setUserData(data);
  }, []);

  /**
   * Fetch the user again for a fresh profile picture link.
   *
   * The picture is served through a signed link that expires after an hour
   * (ZLAYT-1479), but the user is fetched once per session, which lasts much
   * longer. Pictures call this when they fail to load. A failed refetch is
   * ignored: the picture falls back to the initials, and signing out is left
   * to the API calls that need the session.
   */
  const lastImageLinkRenewal = useRef(0);
  const currentAccessToken = useRef(accessToken);
  currentAccessToken.current = accessToken;
  const renewUserImageLink = useCallback(() => {
    const now = Date.now();
    if (
      !accessToken ||
      now - lastImageLinkRenewal.current < IMAGE_LINK_RENEW_INTERVAL_MS
    ) {
      return;
    }
    lastImageLinkRenewal.current = now;
    getUserData(
      accessToken,
      (data: UserResponse) => {
        // Signed out (or another account signed in) while this was in
        // flight: never bring the previous user back.
        if (currentAccessToken.current === accessToken) {
          setUserData(data);
        }
      },
      () => {}
    );
  }, [accessToken]);

  const updateSettings = useCallback((data: UserAISettings) => {
    setUserSettings(data);
  }, []);

  const value = useMemo(
    () => ({
      userData,
      accessToken,
      refreshToken,
      loading,
      login,
      logout,
      userSettings,
      updateSettings,
      updateUserData,
      renewUserImageLink,
    }),
    [
      userData,
      accessToken,
      refreshToken,
      loading,
      login,
      logout,
      userSettings,
      updateSettings,
      updateUserData,
      renewUserImageLink,
    ]
  );

  useEffect(() => {
    if (accessToken) {
      setLoading(true);
      getUserData(
        accessToken,
        (data: UserResponse) => {
          apiHandler.setAuthToken(accessToken);
          console.log('api', data);
          setUserData(data);
          setLoading(false);
        },
        () => {
          logout();
          setLoading(false);
        }
      );
    }
  }, [accessToken]); // logout is stable with empty deps, no need to include

  return (
    <AuthContext.Provider value={value}>
      {/*
        The app is mounted as soon as the user has loaded (ZLAYT-1443, QA
        D-10). It used to wait for the spinner's fade-out and then fade in from
        opacity 0, both driven by animation frames, so opening or refreshing a
        report in a tab without frames (hidden, minimised, covered, or driven
        by an automation agent) left an entirely blank white page.
      */}
      {loading ? <Preloader /> : <div className={styles.page}>{children}</div>}
    </AuthContext.Provider>
  );
};

/**
 * @description Hook to select specific values from AuthContext.
 * Only components using selected values will re-render when those values change.
 * @example
 * const userData = useAuthSelector(state => state.userData);
 * const loading = useAuthSelector(state => state.loading);
 */
export const useAuthSelector = <T,>(
  selector: (value: AuthContextValues) => T
): T => {
  return useContextSelector(AuthContext, selector);
};

/**
 * @description Legacy hook for backward compatibility.
 * @deprecated Use useAuthSelector instead for better performance.
 * This hook causes all components to re-render when any context value changes.
 */
export const useAuth = () => {
  return useContextSelector(AuthContext, (value) => value);
};
